import React from 'react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconArrowRight, IconCheck, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow, NumberMono } from './primitives';
import { EssayBlock } from './types';

interface EssayBodyProps {
  blocks: EssayBlock[];
  onOpen?: () => void;
  isMobile?: boolean;
}

export const EssayBody: React.FC<EssayBodyProps> = ({ blocks, onOpen, isMobile }) => {
  const { t } = useTranslation();
  const totalFilled = blocks.reduce((a, b) => a + b.filled, 0);
  const totalQ = blocks.reduce((a, b) => a + b.total, 0);
  return (
    <Paper withBorder p={isMobile ? 'md' : 'xl'} radius="md">
      <Flex justify="space-between" align="flex-end" mb="lg" gap="md" wrap="wrap">
        <Box>
          <Eyebrow>{t('perfReview.home.essay.section')}</Eyebrow>
          <Title order={3} size="h4" mt={6} style={{ letterSpacing: '-0.01em' }}>
            {t('perfReview.home.essay.title')}
          </Title>
        </Box>
        <Group gap={8} align="baseline">
          <NumberMono style={{ fontSize: 22, fontWeight: 600 }}>{totalFilled}</NumberMono>
          <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('perfReview.home.essay.answeredOf', { total: totalQ })}
          </Text>
        </Group>
      </Flex>
      <Stack gap={10}>
        {blocks.map((b, i) => {
          const pct = b.total > 0 ? b.filled / b.total : 0;
          const isDone = b.filled === b.total;
          const isProgress = !isDone && b.filled > 0;
          const numIcon = isDone ? (
            <ThemeIcon size={28} radius="md" color="teal" variant="filled">
              <IconCheck size={14} />
            </ThemeIcon>
          ) : isProgress ? (
            <ThemeIcon size={28} radius="md" color="orange" variant="light">
              <NumberMono style={{ fontSize: 12, fontWeight: 600 }}>{i + 1}</NumberMono>
            </ThemeIcon>
          ) : (
            <ThemeIcon size={28} radius="md" color="gray" variant="default">
              <NumberMono style={{ fontSize: 12, fontWeight: 600 }}>{i + 1}</NumberMono>
            </ThemeIcon>
          );
          const titleEl = (
            <Box style={{ minWidth: 0 }}>
              <Text size="sm" fw={500}>
                {b.title}
              </Text>
              <Text size="xs" c="dimmed" mt={2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {b.filled} / {b.total}{' '}
                {t('perfReview.home.essay.questionsCount', { count: b.total }).replace(
                  /^\d+\s/,
                  '',
                )}
              </Text>
            </Box>
          );
          const actionBtn = (
            <Button
              size="xs"
              variant="subtle"
              color="teal"
              rightSection={<IconArrowRight size={14} />}
              onClick={onOpen}
            >
              {isDone
                ? t('perfReview.home.essay.actionOpen')
                : isProgress
                  ? t('perfReview.home.essay.actionContinue')
                  : t('perfReview.home.essay.actionStart')}
            </Button>
          );
          if (isMobile) {
            return (
              <Paper
                key={b.id}
                p="sm"
                radius="md"
                withBorder
                style={{ background: 'var(--mantine-color-default-hover)' }}
              >
                <Stack gap="xs">
                  <Flex align="center" gap={12}>
                    {numIcon}
                    {titleEl}
                  </Flex>
                  <Progress value={pct * 100} color="teal" radius="xl" size="sm" />
                  <Flex justify="flex-end">{actionBtn}</Flex>
                </Stack>
              </Paper>
            );
          }
          return (
            <Paper
              key={b.id}
              p="sm"
              radius="md"
              withBorder
              style={{
                background: 'var(--mantine-color-default-hover)',
                display: 'grid',
                gridTemplateColumns: '32px 1fr 140px 110px',
                alignItems: 'center',
                gap: 16,
              }}
            >
              {numIcon}
              {titleEl}
              <Progress value={pct * 100} color="teal" radius="xl" size="sm" />
              <Flex justify="flex-end">{actionBtn}</Flex>
            </Paper>
          );
        })}
      </Stack>
      <Alert
        mt="lg"
        radius="md"
        color="teal"
        variant="light"
        icon={<IconSparkles size={16} />}
        title={null}
      >
        <Text size="sm">
          <Text component="strong" inherit>
            {t('perfReview.home.essay.tipLabel')}
          </Text>{' '}
          {t('perfReview.home.essay.tipBody')}
        </Text>
      </Alert>
    </Paper>
  );
};
